import React, { useRef, useState, useEffect } from 'react';
import { toast } from 'sonner';
import { createPortal } from 'react-dom';
import { X, Reply, Trash2, Play, Send, Check, AlertTriangle, Paperclip } from 'lucide-react';
import { cn } from '@/lib/utils';
import { TimelineComment } from '@/types/video-validation';
import { formatTimeAgo } from '@/utils/timeFormat';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { getInitials, getGradientFromName } from '@/lib/avatar-utils';
import { useAuthStore } from '@/stores/authStore';
import { createVideoComment, deleteDraftComment, updateVideoComment, mapDbCommentToTimelineComment } from '@/services/data/videoComments';
import { uploadAssetFile, createUserAsset } from '@/services/data/userAssets';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
interface VideoCommentsDrawerProps {
  readonly videoId: string;
  readonly videoVersion?: number;
  readonly currentTime: number;
  readonly duration: number;
  readonly initialComments?: TimelineComment[];
  readonly isPlaying: boolean;
  readonly currentUserAvatar?: string;
  readonly currentUserName?: string;
  readonly initialPosition?: 'full' | 'mid' | 'closed';
  readonly videoFolderId?: string;
  readonly embedded?: boolean; // When true, renders inline without portal/backdrop
  readonly isStaffMode?: boolean; // When true, uses staff-specific disclaimers
  readonly onClose: () => void;
  readonly onSeekTo: (time: number) => void;
  readonly onTogglePlay: () => void;
  readonly onCommentsChange?: (comments: TimelineComment[]) => void;
  readonly onSendFeedback?: () => void;
  readonly isSendingFeedback?: boolean;
  readonly focusCommentId?: string | null;
  readonly onCommentAdded?: (commentId: string) => void;
}
export function VideoCommentsDrawer({
  videoId,
  videoVersion = 1,
  currentTime,
  duration,
  initialComments = [],
  isPlaying,
  currentUserAvatar,
  currentUserName = 'Current User',
  initialPosition = 'mid',
  videoFolderId,
  embedded = false,
  isStaffMode = false,
  onClose,
  onSeekTo,
  onTogglePlay,
  onCommentsChange,
  onSendFeedback,
  isSendingFeedback = false,
  focusCommentId = null,
  onCommentAdded
}: VideoCommentsDrawerProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const commentsEndRef = useRef<HTMLDivElement>(null);
  const commentRefs = useRef<{
    [key: string]: HTMLDivElement | null;
  }>({});
  const newCommentInputRef = useRef<HTMLTextAreaElement>(null);
  const {
    userId
  } = useAuthStore();

  // State
  const [comments, setComments] = useState<TimelineComment[]>(initialComments);
  const [drawerPosition, setDrawerPosition] = useState<'full' | 'mid' | 'closed'>(initialPosition);
  const [highlightedComment, setHighlightedComment] = useState<string | null>(null);
  const [newCommentText, setNewCommentText] = useState('');
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [isClosing, setIsClosing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showSendConfirm, setShowSendConfirm] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState('');
  const [attachedFile, setAttachedFile] = useState<File | null>(null);
  const [attachedFilePreview, setAttachedFilePreview] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  // Height classes for each position
  const getHeightClass = () => {
    switch (drawerPosition) {
      case 'full':
        return 'h-[75vh]';
      case 'mid':
        return 'h-[28vh]';
      case 'closed':
        return 'h-0 overflow-hidden';
    }
  };

  // Cycle through positions on handle click
  const cyclePosition = () => {
    setDrawerPosition(prev => {
      if (prev === 'mid') return 'full';
      if (prev === 'full') return 'mid';
      return 'mid';
    });
  };

  // Sync with initialComments when they change
  useEffect(() => {
    setComments(initialComments);
  }, [initialComments]);

  // Sync drawer position with initialPosition prop
  useEffect(() => {
    setDrawerPosition(initialPosition);
  }, [initialPosition]);

  // Auto-focus on the comment input when drawer opens
  useEffect(() => {
    const timer = setTimeout(() => {
      newCommentInputRef.current?.focus();
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  // Auto-resize textarea as user types
  useEffect(() => {
    const el = newCommentInputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, [newCommentText]);

  // Notify parent of comment changes
  useEffect(() => {
    onCommentsChange?.(comments);
  }, [comments, onCommentsChange]);

  const handleMarkerClick = (timestamp: number, commentId: string) => {
    onSeekTo(timestamp);
    setDrawerPosition('full');
    setTimeout(() => {
      commentRefs.current[commentId]?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
      setHighlightedComment(commentId);
      setTimeout(() => setHighlightedComment(null), 3000);
    }, 100);
  };

  // Focus a comment from external trigger (e.g. marker click in player)
  useEffect(() => {
    if (!focusCommentId) return;
    setDrawerPosition('full');
    setTimeout(() => {
      commentRefs.current[focusCommentId]?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
      setHighlightedComment(focusCommentId);
      setTimeout(() => setHighlightedComment(null), 3000);
    }, 100);
  }, [focusCommentId]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setAttachedFile(file);
      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => setAttachedFilePreview(e.target?.result as string);
        reader.readAsDataURL(file);
      } else {
        setAttachedFilePreview(null);
      }
    }
  };

  const handleRemoveAttachment = () => {
    if (attachedFilePreview?.startsWith('blob:')) {
      URL.revokeObjectURL(attachedFilePreview);
    }
    setAttachedFile(null);
    setAttachedFilePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleAddComment = async () => {
    if (!newCommentText.trim() && !attachedFile) return;
    if (!userId) {
      console.error('handleAddComment: userId is null, user not authenticated');
      toast.error('Vous devez être connecté pour envoyer un retour');
      return;
    }
    setIsSaving(true);
    setIsUploading(!!attachedFile);
    
    try {
      let assetId: string | undefined;
      let assetUrl: string | undefined;
      let assetFileName: string | undefined;

      // Upload asset if attached
      if (attachedFile) {
        const uploadResult = await uploadAssetFile(userId, attachedFile);
        // Create user_asset record linked to video folder
        const asset = await createUserAsset({
          user_id: userId,
          file_name: attachedFile.name,
          file_path: uploadResult.filePath,
          file_type: attachedFile.type,
          file_size: attachedFile.size,
          folder_id: videoFolderId || null,
        });
        assetId = asset.id;
        assetUrl = uploadResult.signedUrl || uploadResult.publicUrl;
        assetFileName = attachedFile.name;
      }

      // Create comment with optional asset_id
      const result = await createVideoComment({
        video_content_id: videoId,
        user_id: userId,
        comment_text: newCommentText.trim() || (attachedFile ? `📎 ${attachedFile.name}` : ''),
        timestamp_seconds: Math.round(currentTime),
        video_version: videoVersion,
        asset_id: assetId,
      });

      if (result.success && result.data) {
        const newComment: TimelineComment = {
          id: result.data.id,
          dbId: result.data.id,
          timestamp: result.data.timestamp_seconds || 0,
          text: result.data.comment_text,
          author: currentUserName,
          authorAvatar: currentUserAvatar || undefined,
          createdAt: new Date(result.data.created_at),
          isSent: false,
          userId: result.data.user_id,
          assetId,
          assetUrl,
          assetFileName,
          assetFileType: attachedFile?.type,
        };
        setComments(prev => [...prev, newComment]);
        setNewCommentText('');
        setAttachedFile(null);
        setAttachedFilePreview(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        setDrawerPosition('full');
        // Scroll to new comment and notify parent to activate marker
        onCommentAdded?.(newComment.id);
        setTimeout(() => {
          commentRefs.current[newComment.id]?.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }, 150);
      } else {
        toast.error('Impossible d\'envoyer le commentaire');
      }
    } catch (error) {
      console.error('Error adding comment:', error);
    } finally {
      setIsSaving(false);
      setIsUploading(false);
    }
  };
  const handleAddReply = async (parentId: string) => {
    if (!replyText.trim() || !userId) return;
    const parentComment = comments.find(c => c.id === parentId);
    if (!parentComment) return;
    setIsSaving(true);
    try {
      // Immediately persist reply to database as draft
      const result = await createVideoComment({
        video_content_id: videoId,
        user_id: userId,
        comment_text: replyText.trim(),
        timestamp_seconds: Math.round(parentComment.timestamp),
        video_version: videoVersion,
        parent_comment_id: parentComment.dbId || parentId
      });
      if (result.success && result.data) {
        const newReply: TimelineComment = {
          id: result.data.id,
          dbId: result.data.id,
          timestamp: result.data.timestamp_seconds || 0,
          text: result.data.comment_text,
          author: currentUserName,
          authorAvatar: currentUserAvatar || undefined,
          createdAt: new Date(result.data.created_at),
          parentId: parentComment.dbId || parentId,
          isSent: false,
          userId: result.data.user_id
        };
        setComments(prev => [...prev, newReply]);
        setReplyText('');
        setReplyTo(null);
      }
    } catch (error) {
      console.error('Error adding reply:', error);
    } finally {
      setIsSaving(false);
    }
  };
  const handleDeleteComment = async (commentId: string) => {
    const comment = comments.find(c => c.id === commentId);
    // Only allow deletion of own unsent comments
    if (!comment || comment.isSent) return;
    try {
      // Delete from database
      if (comment.dbId) {
        await deleteDraftComment(comment.dbId);
      }

      // Remove from local state (including replies)
      setComments(prev => prev.filter(c => c.id !== commentId && c.parentId !== commentId));
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };

  const handleStartEdit = (comment: TimelineComment) => {
    if (comment.isSent) return;
    setEditingCommentId(comment.id);
    setEditingText(comment.text);
  };

  const handleSaveEdit = async (commentId: string) => {
    const comment = comments.find(c => c.id === commentId);
    if (!comment || !editingText.trim() || comment.isSent) return;
    
    setIsSaving(true);
    try {
      if (comment.dbId) {
        await updateVideoComment(comment.dbId, { comment_text: editingText.trim() });
      }
      setComments(prev => prev.map(c => 
        c.id === commentId ? { ...c, text: editingText.trim() } : c
      ));
      setEditingCommentId(null);
      setEditingText('');
    } catch (error) {
      console.error('Error updating comment:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingCommentId(null);
    setEditingText('');
  };
  const handleClose = (resumePlayback = false) => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      if (resumePlayback) {
        onTogglePlay();
      }
    }, 300);
  };
  const parentComments = comments.filter(c => !c.parentId);
  const unsentCount = comments.filter(c => !c.isSent).length;
  const hasSentBatch = comments.some(c => c.isSent === true);
  const isSecondBatch = hasSentBatch && unsentCount > 0;

  // Computed values for dialog header styling
  const getHeaderBackgroundClass = () => {
    if (isStaffMode) return "bg-amber-500/20";
    if (isSecondBatch) return "bg-warning/20";
    return "bg-amber-500/20";
  };
  const headerBackgroundClass = getHeaderBackgroundClass();

  const getHeaderIconClass = () => {
    if (isStaffMode) return "text-amber-400";
    if (isSecondBatch) return "text-warning";
    return "text-amber-400";
  };
  const headerIconClass = getHeaderIconClass();

  // Computed values for dialog title
  const getDialogTitle = () => {
    if (isStaffMode) return "Un seul retour ?";
    if (isSecondBatch) return "Nouveau retour";
    return "Confirmer l'envoi";
  };
  const dialogTitle = getDialogTitle();

  // Computed values for cancel button text
  const getCancelButtonText = () => {
    if (isStaffMode) return "Ajouter d'autres retours";
    if (isSecondBatch) return "Continuer plus tard";
    return "Annuler";
  };
  const cancelButtonText = getCancelButtonText();

  // Computed values for action button styling
  const getActionButtonClass = () => {
    if (isSecondBatch && !isStaffMode) return "bg-warning hover:bg-warning/80";
    return "bg-white hover:bg-white/90";
  };
  const actionButtonClass = getActionButtonClass();

  // Computed values for action button text
  const getActionButtonText = () => {
    if (isStaffMode) return "Envoyer quand même";
    if (isSecondBatch) return "Envoyer quand même";
    return "Envoyer mes retours";
  };
  const actionButtonText = getActionButtonText();

  const handleBackdropClick = () => {
    handleClose(true);
  };

  const drawerContent = (
      <>
      {/* Backdrop - only visible in full position, non-embedded */}
      {!embedded && drawerPosition === 'full' && (
        <button
          className={cn("fixed inset-0 z-[210] bg-black/60 transition-opacity duration-300", isClosing ? 'opacity-0' : 'opacity-100')}
          onClick={handleBackdropClick}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              handleBackdropClick();
            }
          }}
          aria-label="Close comments drawer"
          type="button"
        />
      )}
      
      {/* Drawer */}
      <div className={cn(
        embedded
          ? "h-full flex flex-col bg-transparent"
          : cn("fixed bottom-0 left-0 right-0 z-[211] bg-black/95 backdrop-blur-xl rounded-t-3xl shadow-2xl transition-all duration-300 ease-out", getHeightClass(), isClosing && 'translate-y-full opacity-0')
      )}>
        {/* Handle - hidden in embedded mode */}
        {!embedded && (
        <div className="w-full py-3 flex justify-center cursor-pointer hover:bg-white/5 rounded-t-3xl" onClick={cyclePosition}>
          <div className="w-12 h-1 bg-white/20 rounded-full" />
        </div>
        )}

        <div className={cn("h-full flex flex-col overflow-hidden", embedded ? "px-1 pb-3" : "px-4 pb-8")}>
          {/* Header */}
          <div className="mb-2">
            
          </div>

          {/* Playbar removed — using the video player's playbar instead */}

          {/* Comments List - only visible in full position or embedded */}
          {(embedded || drawerPosition === 'full') && <div className={cn("flex-1 min-h-0 pb-4 w-full", parentComments.length === 0 ? "flex flex-col" : "overflow-y-auto space-y-4")}>
            {parentComments.length === 0 ? <div className="flex-1 flex items-center justify-center text-white/60 text-sm">
                Faites votre premier retour
              </div> : parentComments.map(comment => {
            const replies = comments.filter(c => c.parentId === comment.id || c.parentId === comment.dbId);
            const isHighlighted = highlightedComment === comment.id;
            const isSent = comment.isSent;
            const isOwnComment = comment.userId === userId;
            return <div key={comment.id} ref={el => {
              commentRefs.current[comment.id] = el;
            }} className="py-4 cursor-pointer" onClick={() => onSeekTo(comment.timestamp)}>
                    {/* Comment Header */}
                    {/* Comment Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <Avatar className="w-7 h-7 border border-white/20">
                          <AvatarImage src={comment.authorAvatar} alt={comment.author} />
                          <AvatarFallback 
                            className="text-white text-xs font-semibold"
                            style={{ background: getGradientFromName(comment.author.split(' ')[0], comment.author.split(' ').slice(1).join(' ')) }}
                          >
                            {getInitials(comment.author.split(' ')[0], comment.author.split(' ').slice(1).join(' '))}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="text-sm text-white">
                            <span className="font-semibold">{comment.author}</span>
                            <span className="text-white/50 ml-2">{formatTimeAgo(comment.createdAt)}</span>
                          </p>
                          <p className="text-xs text-white/40 font-mono">{`${Math.floor(comment.timestamp / 60).toString().padStart(2, '0')}:${Math.floor(comment.timestamp % 60).toString().padStart(2, '0')}`}</p>
                        </div>
                      </div>
                      {isSent && <Badge variant="outline" className="text-[10px] px-1.5 py-0.5 border-orange-500/50 text-orange-400 bg-orange-500/10">
                          <Check className="h-2.5 w-2.5 mr-0.5" />
                          Envoyé
                        </Badge>}
                    </div>

                    {/* Comment Text or Edit Input */}
                    {editingCommentId === comment.id ? (
                      <div className="mb-3">
                        <Textarea
                          value={editingText}
                          onChange={e => setEditingText(e.target.value)}
                          className="min-h-[60px] text-sm !bg-transparent border border-white/20 text-white resize-none focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-white/40"
                          autoFocus
                          disabled={isSaving}
                          onKeyDown={e => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleSaveEdit(comment.id);
                            }
                            if (e.key === 'Escape') {
                              handleCancelEdit();
                            }
                          }}
                        />
                        <div className="flex gap-2 mt-2">
                          <button onClick={() => handleSaveEdit(comment.id)} disabled={!editingText.trim() || isSaving} className="text-sm text-white/60 hover:text-white transition-colors disabled:opacity-50">
                            Enregistrer
                          </button>
                          <span className="text-white/20">|</span>
                          <button onClick={handleCancelEdit} className="text-sm text-white/40 hover:text-white/60 transition-colors">
                            Annuler
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="mb-3 space-y-3">
                        <p className={cn("text-sm whitespace-pre-wrap leading-relaxed", isSent ? "text-white/70" : "text-white")}>
                          {comment.text}
                        </p>
                        {/* Asset Preview - displayed below comment text */}
                        {comment.assetUrl && (
                          <a 
                            href={comment.assetUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="block"
                          >
                            {comment.assetFileType?.startsWith('image/') ? (
                              <img 
                                src={comment.assetUrl} 
                                alt={comment.assetFileName || 'Attachment'} 
                                className="max-w-[200px] max-h-[150px] rounded-lg object-cover border border-white/20"
                              />
                            ) : (
                              <div className="flex items-center gap-2 p-2 bg-white/10 rounded-lg max-w-[200px]">
                                <Paperclip className="h-4 w-4 text-white/60 flex-shrink-0" />
                                <span className="text-sm text-white/70 truncate">{comment.assetFileName || 'Fichier joint'}</span>
                              </div>
                            )}
                          </a>
                        )}
                      </div>
                    )}

                    {/* Comment Actions */}
                    {editingCommentId !== comment.id && (
                      <div className="flex items-center gap-2">
                        <button onClick={() => setReplyTo(comment.id)} className="text-sm text-white/40 hover:text-white/60 transition-colors">
                          Répondre
                        </button>
                        {isOwnComment && !isSent && <>
                          <span className="text-white/20">|</span>
                          <button onClick={() => handleStartEdit(comment)} className="text-sm text-white/40 hover:text-white/60 transition-colors">
                            Éditer
                          </button>
                          <span className="text-white/20">|</span>
                          <button onClick={() => handleDeleteComment(comment.id)} className="text-sm text-white/40 hover:text-white/60 transition-colors">
                            Supprimer
                          </button>
                        </>}
                      </div>
                    )}

                    {/* Reply Input */}
                    {replyTo === comment.id && <div className="mt-3 flex gap-2 items-center">
                        <input type="text" value={replyText} onChange={e => setReplyText(e.target.value)} placeholder="Votre réponse..." className="flex-1 h-10 px-3 !bg-transparent border border-white/20 rounded-md text-white text-sm placeholder:text-white/60 focus:outline-none focus:border-white/20" autoFocus disabled={isSaving} onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddReply(comment.id);
                  }
                  if (e.key === 'Escape') {
                    setReplyTo(null);
                  }
                }} />
                        <Button onClick={() => handleAddReply(comment.id)} disabled={!replyText.trim() || isSaving} size="icon" className="rounded-full h-7 w-7 flex-shrink-0 bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 disabled:opacity-50">
                          <Send className="h-3.5 w-3.5 text-white" />
                        </Button>
                      </div>}

                    {/* Replies */}
                    {replies.length > 0 && <div className="mt-2 pl-3 space-y-2">
                        {replies.map(reply => {
                  const isReplySent = reply.isSent;
                  const isOwnReply = reply.userId === userId;
                  return <div key={reply.id} className="rounded-xl p-3">
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <Avatar className="w-7 h-7 border border-white/20">
                                    <AvatarImage src={reply.authorAvatar} alt={reply.author} />
                                    <AvatarFallback 
                                      className="text-white text-xs font-semibold"
                                      style={{ background: getGradientFromName(reply.author.split(' ')[0], reply.author.split(' ').slice(1).join(' ')) }}
                                    >
                                      {getInitials(reply.author.split(' ')[0], reply.author.split(' ').slice(1).join(' '))}
                                    </AvatarFallback>
                                  </Avatar>
                                  <div>
                                    <p className="text-xs font-semibold text-white">{reply.author}</p>
                                    <p className="text-[10px] text-white/60">{formatTimeAgo(reply.createdAt)}</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  {isReplySent && <Badge variant="outline" className="text-[10px] px-1.5 py-0.5 border-orange-500/50 text-orange-400 bg-orange-500/10">
                                      <Check className="h-2.5 w-2.5 mr-0.5" />
                                      Envoyé
                                    </Badge>}
                                  {isOwnReply && !isReplySent && <button onClick={() => handleDeleteComment(reply.id)} className="text-sm text-white/40 hover:text-white/60 transition-colors">
                                      Supprimer
                                    </button>}
                                </div>
                              </div>
                              <p className={cn("text-xs whitespace-pre-wrap", isReplySent ? "text-white/60" : "text-white/80")}>{reply.text}</p>
                            </div>;
                })}
                      </div>}
                  </div>;
          })}
            <div ref={commentsEndRef} />
          </div>}

          {/* Add Comment - at the bottom */}
          <div className="flex-none mt-0">
            {/* Attachment Preview */}
            {attachedFile && (
              <div className="mb-2 p-2 bg-white/5 rounded-lg flex items-center gap-2">
                {attachedFilePreview ? (
                  <img src={attachedFilePreview} alt="Preview" className="w-12 h-12 object-cover rounded" />
                ) : (
                  <div className="w-12 h-12 bg-white/10 rounded flex items-center justify-center">
                    <Paperclip className="h-5 w-5 text-white/60" />
                  </div>
                )}
                <span className="flex-1 text-sm text-white/70 truncate">{attachedFile.name}</span>
                <button 
                  onClick={handleRemoveAttachment}
                  className="p-1 hover:bg-white/10 rounded"
                >
                  <X className="h-4 w-4 text-white/60" />
                </button>
              </div>
            )}
            <div className="flex gap-2 items-center">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*,.pdf,.doc,.docx"
                onChange={handleFileSelect}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isSaving || isUploading}
                className="p-1 hover:bg-white/10 rounded-lg transition-colors disabled:opacity-50 flex-shrink-0"
              >
                <Paperclip className="h-4 w-4 text-white/60" />
              </button>
              <textarea ref={newCommentInputRef} autoFocus value={newCommentText} onChange={e => setNewCommentText(e.target.value)} placeholder="Votre retour..." className="flex-1 min-h-[36px] max-h-[120px] text-sm !bg-transparent border border-white/20 text-white placeholder:text-white/40 resize-none overflow-y-auto focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-white/20 rounded-md px-3 py-2 outline-none" rows={1} disabled={isSaving || isUploading} onFocus={() => { if (isPlaying) onTogglePlay(); }} onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleAddComment();
              }
            }} />
              <Button onClick={handleAddComment} disabled={(!newCommentText.trim() && !attachedFile) || isSaving || isUploading} size="icon" className="rounded-full h-8 w-8 flex-shrink-0 bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 disabled:opacity-50">
                {isUploading ? (
                  <div className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <Send className="h-5 w-5 text-white" />
                )}
              </Button>
            </div>
          </div>

          {/* Fixed footer with send button - always visible, disabled when no unsent comments */}
          <div className={cn("pt-2 mt-1 flex-none", embedded ? "px-0 pb-0" : "px-0 pb-0")}>
            <Button onClick={() => setShowSendConfirm(true)} disabled={unsentCount === 0 || isSendingFeedback} className="w-full h-10 bg-white text-black font-medium text-sm rounded-lg hover:bg-white/90 disabled:opacity-50 disabled:bg-white/20 disabled:text-white/50">
              {isSendingFeedback ? "Envoi..." : "Envoyer mes retours"}
            </Button>
          </div>

        </div>
      </div>

      {/* Send confirmation dialog */}
      <AlertDialog open={showSendConfirm} onOpenChange={setShowSendConfirm}>
        <AlertDialogContent className="bg-black/95 border-white/10 text-white">
          <AlertDialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className={cn("p-2 rounded-full", headerBackgroundClass)}>
                <AlertTriangle className={cn("h-5 w-5", headerIconClass)} />
              </div>
              <AlertDialogTitle>
                {dialogTitle}
              </AlertDialogTitle>
            </div>
            <AlertDialogDescription className="text-white/70 space-y-3">
              {isStaffMode ? (
                unsentCount === 1 ? <>
                  <p>
                    Vous n'avez qu'<strong className="text-white">un seul retour</strong> à envoyer.
                  </p>
                  <p>
                    Pensez à regrouper <strong className="text-amber-400">tous vos retours</strong> avant d'envoyer pour faciliter le travail du monteur.
                  </p>
                  <p className="text-white/50 text-xs">
                    Vous pouvez continuer à ajouter des retours et tout envoyer d'un coup.
                  </p>
                </> : <>
                  <p>
                    Vous allez envoyer <strong className="text-white">{unsentCount} retours</strong> au monteur.
                  </p>
                  <p className="text-white/50 text-xs">
                    Assurez-vous d'avoir ajouté tous vos retours avant d'envoyer.
                  </p>
                </>
              ) : isSecondBatch ? <>
                  <p>
                    Vous avez déjà envoyé un premier lot de retours sur cette vidéo.
                  </p>
                  <p>
                    Ce nouveau lot sera considéré comme une <strong className="text-white">demande complémentaire</strong> et sera facturé <strong className="text-warning">50€ HT</strong>.
                  </p>
                  <p className="text-white/50 text-xs">
                    Vous pouvez aussi continuer à ajouter des retours et les envoyer plus tard.
                  </p>
                </> : <>
                  <p>
                    Un seul lot de demandes de modifications est inclus dans votre formule. Merci de vérifier que tous vos retours ont bien été enregistrés avant l'envoi.
                  </p>
                </>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/10 border-white/10 text-white/60 hover:bg-white/20 hover:text-white/80">
              {cancelButtonText}
            </AlertDialogCancel>
            <AlertDialogAction onClick={(e) => {
            e.preventDefault();
            setShowSendConfirm(false);
            onSendFeedback?.();
          }} className={cn("text-black", actionButtonClass)}>
              {actionButtonText}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );

  if (embedded) {
    return drawerContent;
  }

  return createPortal(drawerContent, document.body);
}